import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { LabelNodeModel } from "@/models/nodes/LabelNodeModel";
import { OperatorType } from "@/models/nodes/operators";
import { SymbolNodeModel } from "@/models/nodes/SymbolNodeModel";

import type { NodeModel } from "@/models/nodes/types";

function nodeIsNumerator(node: NodeModel) {
  return node.parent?.is(OperatorType.FRACTION) && node.parent.parameters[0]?.id === node.id;
}

function nodeIsBodyOfSquareRoot(node: NodeModel) {
  return node.parent?.is(OperatorType.ROOT) && node.parent.parameters[1]?.id === node.id;
}

function nodeIsBottomLabelOfFunction(node: NodeModel) {
  const parentSymbolNode = node.ancestors.find((n) => n.is(SymbolNodeModel));

  return (
    parentSymbolNode?.parent?.is(FunctionNodeModel) &&
    node.parent?.is(LabelNodeModel) &&
    (node.parent.position === "right-bottom" || node.parent.position === "left-bottom")
  );
}

function nodeIsSingleTopLabelOfFunction(node: NodeModel) {
  const parentSymbolNode = node.ancestors.find((n) => n.is(SymbolNodeModel));

  if (!parentSymbolNode?.parent?.is(FunctionNodeModel)) return false;
  if (!node.parent?.is(LabelNodeModel)) return false;

  const isTopLabel = node.parent.position === "right-top" || node.parent.position === "left-top";
  if (!isTopLabel) return false;

  const hasBottomLabel = parentSymbolNode.labelNodes.some(
    (labelNode) => labelNode.position === "right-bottom" || labelNode.position === "left-bottom",
  );
  return !hasBottomLabel;
}

/**
 * Check if an inline node is in a position where it should inherit
 * its parent's `contextId` rather than getting its own.
 */
function isInheritedContextPosition(node: NodeModel): boolean {
  return !!(
    nodeIsNumerator(node) ||
    nodeIsBodyOfSquareRoot(node) ||
    nodeIsBottomLabelOfFunction(node) ||
    nodeIsSingleTopLabelOfFunction(node)
  );
}

/**
 * Computes the nesting level of a node by counting nested ancestors.
 */
export function getNodeLevel(node: NodeModel): number {
  let level = 0;

  for (const ancestor of node.ancestors) {
    if (ancestor.isNested) {
      level += 1;
    }
  }

  return level;
}

/**
 * Computes `contextId` for an `InlineNodeModel` by walking up the tree.
 *
 * Rules:
 * - If the inline's direct parent is nested AND the inline isn't in a special position,
 *   the inline gets its own id as contextId (creating a navigation boundary).
 * - Otherwise, the inline inherits the contextId from its nearest ancestor InlineNode.
 * - At the root (no ancestor InlineNode), contextId is "".
 */
export function getContextId(inlineNode: InlineNodeModel): string {
  const { parent } = inlineNode;

  if (!parent) return "";

  if (parent.isNested && !isInheritedContextPosition(inlineNode)) {
    return inlineNode.id;
  }

  const parentInline = parent.ancestors.find((a) => a.is(InlineNodeModel));
  if (parentInline?.is(InlineNodeModel)) {
    return getContextId(parentInline);
  }

  return "";
}
